/**
 * Calls to our own API — the only way the browser reads or writes data (D-026).
 *
 * Locally the path is `/api` and Vite proxies it to the Functions host, so the
 * app never needs to know a hostname while developing. A deployed build gets
 * `VITE_API_BASE` from the workflow - the Function App's own hostname - because
 * the Static Web Apps are on the Free plan, which cannot link a backend (D-024).
 * That call is cross-origin, so each Function App allows its SWA's origin in
 * CORS; that is configured in Azure and asserted by the deploy's smoke test,
 * not handled here.
 *
 * Every call carries the session's access token as `Authorization: Bearer`.
 * The API verifies it by signature and answers 401 to anything else; a 401
 * here is therefore "sign in again", never "the server is broken" — the caller
 * (the session provider) treats it that way. A 403 is different: signed in,
 * not allowed. The page says so and nobody gets signed out.
 *
 * One function per endpoint, typed by the shared response interfaces, so a
 * page never spells a path or a shape itself.
 */

import type {
  ClientsResponse,
  CustomFieldSaveBody,
  CustomFieldsResponse,
  ConvertQuoteBody,
  CreateQuoteRequestBody,
  PropertiesResponse,
  QuoteConversion,
  QuoteCreateRequest,
  QuoteDraft,
  QuoteDraftCreatedResponse,
  QuoteDraftDiscardedResponse,
  QuoteDraftFieldSaveBody,
  QuoteDraftSavedResponse,
  QuoteDraftSendCheckResponse,
  QuoteDraftSendResponse,
  QuoteDraftsResponse,
  QuoteDocumentFileResponse,
  SaveDraftQuoteLinesBody,
  SaveQuoteDraftBody,
  SendQuoteDraftBody,
  QuoteRequestOptionsResponse,
  NotificationDeliveriesResponse,
  NotificationsResponse,
  SetNotificationPausedRequest,
  SetNotificationRecipientsRequest,
  OpsTrackerRowsResponse,
  SetUserNotificationsRequest,
  SetUserNotificationsResponse,
  UsersNotificationsResponse,
  ErrorResponse,
  JobberDisconnectResponse,
  OkResponse,
  AdminUsersResponse,
  BackfillActionRequest,
  BackfillStatusResponse,
  HealthResponse,
  JobberConnectRequest,
  JobberConnectResponse,
  JobberCommandsResponse,
  JobberConnectionPurpose,
  JobberPurposeRequest,
  JobberStatusResponse,
  JobberTestResponse,
  JobCommunicationsResponse,
  JobDetail,
  JobDocumentsResponse,
  JobFinancialsResponse,
  JobLaborResponse,
  JobLineItemsResponse,
  JobListResponse,
  JobOptionsResponse,
  QuoteDetail,
  ProductsResponse,
  QuoteLinePhotosResponse,
  QuoteLinesResponse,
  SaveQuoteLinesBody,
  QuoteListResponse,
  QuoteOptionsResponse,
  QuoteSettings,
  QuoteWorksheetResponse,
  ImportQuoteWorksheetBody,
  QuoteImportResponse,
  QuoteImportReadsResponse,
  ImportPurchaseOrderBody,
  PurchaseOrderImportResponse,
  PurchaseOrderImportReadsResponse,
  SaveQuoteSettingsBody,
  SaveQuoteWorksheetBody,
  NoteStreamResponse,
  JobScheduleResponse,
  MeResponse,
  PeopleResponse,
  PersonLinksResponse,
  SetPersonLinkRequest,
  SetPersonLinkResponse,
  AccountFromJobberRequest,
  AccountFromJobberResponse,
  PreferenceResponse,
  PullResponse,
  RefetchesResponse,
  RefetchRequest,
  Role,
  ScheduleResponse,
  SetOnScheduleRequest,
  SetGroupRequest,
  CalendarSettings,
  CalendarGroup,
  SetRolesRequest,
  SetRolesResponse,
  ShadowClearResponse,
  ScheduleLinkRewriteResponse,
  ScheduleLinkWriteResponse,
  NotesLinkRewriteResponse,
  NotesLinkWriteResponse,
  CreateVisitRequestBody,
  CreateVisitRequestResponse,
  AppendWorkDaysBody,
  AppendWorkDaysResponse,
  SetWorkDaysBody,
  SetWorkDaysResponse,
  CreateWorkItemBody,
  SaveWorkItemBody,
  SaveWorkItemResponse,
  SetWorkDatesHiddenBody,
  SetWorkDatesHiddenResponse,
  SetWorkItemGroupBody,
  SetWorkItemGroupResponse,
  WorkItem,
  WorkItemTracker,
  SaveWorkTypeBody,
  WorkTypesResponse,
  CorrectWorkEntryBody,
  SplitWorkEntryBody,
  WorkItemBreakdown,
  CreateTechNoteBody,
  CreateTechNoteResponse,
  StartTechNoteBody,
  SubmitTechNoteResponse,
  TechNoteContextResponse,
  JobSovResponse,
  SaveSovItemBody,
  SetSovPlanLinkBody,
  SovItem,
  SovItemHistory,
  AddPurchaseOrderNoteBody,
  CreatePurchaseOrderBody,
  PaymentBody,
  PurchaseOrderDetail,
  PurchaseOrderNote,
  ReceiptResponse,
  PurchaseOrderSettings,
  EmailTemplate,
  EmailTemplates,
  Profile,
  PurchaseOrdersResponse,
  SavePurchaseOrderSettingsBody,
  PurchaseOrderView,
  ReceiptBody,
  SavePurchaseOrderBody,
  EmailDraftResponse,
  EmailKind,
  SendEmailBody,
  SentEmail,
  SentEmailsResponse,
  CreateVendorBody,
  UpdateVendorBody,
  Vendor,
  VendorsResponse,
  VersionBody,
  BillingNoteBody,
  JobBillingListResponse,
  JobberOptionPeopleResponse,
  SetJobberOptionBody,
  JobBillingNote,
  JobBillingResponse,
  JobBillingView,
  JobBillingKindTab,
  SaveJobBillingBody,
  ChangeOrderConvertResponse,
  ChangeOrderCreatedResponse,
  ChangeOrdersResponse,
  ChangeOrderStatusBody,
  ConvertChangeOrderBody,
  CreateChangeOrderBody,
  EditChangeOrderBody,
  FileConversionsResponse,
  BrexCommandsResponse,
  BrexExpenseResponse,
  BrexReceiptBody,
  BrexReceiptCreated,
  BrexStatusResponse,
  FileFetchActionResponse,
  FileFetchRetryBody,
  FileFetchStatusResponse,
  TodoDefault,
  TodoDefaultsResponse,
  CreateTodoDefaultBody,
  UpdateTodoDefaultBody,
  OrderTodoDefaultsBody,
  JobTodosResponse,
  QuoteTodosResponse,
  PurchaseOrderTodosResponse,
  PurchaseOrderDraft,
  PurchaseOrderDraftsResponse,
  PurchaseOrderDraftTodosResponse,
  StartPurchaseOrderDraftBody,
  SavePurchaseOrderDraftBody,
  CreateFromPurchaseOrderDraftBody,
  TodoLink,
  AddTodoLinkBody,
  SetTodoProofBody,
  FileType,
  FileTypesResponse,
  CreateFileTypeBody,
  UpdateFileTypeBody,
  OrderFileTypesBody,
  SetFileTypeBody,
  FileSearchResponse,
  LinkFilesBody,
  LinkFilesResponse,
  VisitRequest,
  VisitRequestContextResponse,
  WebhookPayloadResponse,
  WebhooksResponse,
  AccountsResponse,
  EmployeesResponse,
  CreateHeadlineBody,
  CreateIssueBody,
  CreateMeetingBody,
  CreateTodoBody,
  AttachKind,
  AttachedFileResponse,
  FileListResponse,
  FileLinkResponse,
  StartUploadBody,
  StartUploadResponse,
  HeadlineListResponse,
  HeadlineResponse,
  ScorecardResponse,
  IssueHistoryResponse,
  IssueListResponse,
  IssueResponse,
  TodoHistoryResponse,
  LegacyMeetingResponse,
  LegacyMeetingsResponse,
  MeetingImportBody,
  MeetingImportResponse,
  MeetingListResponse,
  MeetingTodosResponse,
  MeetingPrioritiesResponse,
  MakePriorityBody,
  SavePriorityBody,
  PriorityStatusBody,
  QuarterlyPriorityResponse,
  MeetingResponse,
  SaveHeadlineBody,
  SaveIssueBody,
  SaveMeetingBody,
  SaveTodoBody,
  ReviewUnlinkedTodoBody,
  ReviewUnlinkedTodoResponse,
  SetAttendeesBody,
  TodoListResponse,
  TodoResponse,
  AgendaSection,
  CompleteMeetingBody,
  LiveMeetingResponse,
  CombineIssuesBody,
  OutcomeBody,
  PastMeetingsResponse,
  PreviousHeadlinesResponse,
  ReviewTodoBody,
  RevisionResponse,
  SaveNoteBody,
  SaveRoundBody,
  SaveSegueBody,
  SetPausedBody,
  SetSectionBody,
  StartMeetingResponse,
} from '@unitedos/shared';
import { currentAccessToken } from './supabase.js';
import type { ThemeChoice } from './theme.js';

const configured = import.meta.env.VITE_API_BASE as string | undefined;
export const API_BASE = configured?.trim() ? configured.trim().replace(/\/+$/, '') : '/api';

/** Thrown for any non-2xx or unreadable response, with something a human can act on. */
export class ApiError extends Error {
  constructor(
    message: string,
    readonly status?: number,
    /** The API's own `error` code from the body, when it sent one. */
    readonly code?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** The API refused the session. The only fix is signing in again. */
  get unauthorized(): boolean {
    return this.status === 401;
  }

  /** Signed in, but this person may not do this. Not a reason to sign out. */
  get forbidden(): boolean {
    return this.status === 403;
  }
}

/**
 * The headers a request carries for a given token. Pure, so it is tested:
 * a token present means exactly one `Authorization` header, and no token
 * means no header at all rather than `Bearer null`.
 */
export function authorizationHeaders(token: string | null): Record<string, string> {
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/**
 * A human-readable message for a failed response. Pure. When the API sent a
 * `reason` (the sentence a refusal carries — "another handoff item has that
 * name"), that is the message; otherwise the API's own error code — short,
 * stable and greppable in the Function logs — rather than prose invented here.
 */
export function describeFailure(path: string, status: number, body: unknown): { message: string; code?: string } {
  const code =
    typeof body === 'object' && body !== null && typeof (body as Partial<ErrorResponse>).error === 'string'
      ? (body as ErrorResponse).error
      : undefined;
  if (status === 401) return { message: 'Your session is no longer valid. Sign in again.', code: code ?? 'unauthorized' };
  if (status === 403) return { message: 'You do not have access to this.', code: code ?? 'forbidden' };
  const reason = typeof body === 'object' && body !== null && typeof (body as Partial<ErrorResponse>).reason === 'string' ? (body as ErrorResponse).reason : undefined;
  if (reason) return code ? { message: reason, code } : { message: reason };
  return code
    ? { message: `${API_BASE}${path} returned ${status} (${code})`, code }
    : { message: `${API_BASE}${path} returned ${status}` };
}

interface RequestOptions {
  readonly method?: 'GET' | 'PUT' | 'POST' | 'DELETE';
  readonly body?: unknown;
  /** A multipart body (a file sent with the request, D-235) in place of JSON; the browser sets its own Content-Type with the boundary. */
  readonly form?: FormData;
  readonly signal?: AbortSignal;
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const token = await currentAccessToken();
  const headers: Record<string, string> = { ...authorizationHeaders(token) };
  if (options.body !== undefined) headers['Content-Type'] = 'application/json';

  let response: Response;
  try {
    response = await fetch(`${API_BASE}${path}`, {
      method: options.method ?? 'GET',
      headers,
      // Built conditionally rather than `{ signal }`: tsconfig sets
      // exactOptionalPropertyTypes, so passing an explicit `undefined` is a
      // type error, not a no-op.
      ...(options.body !== undefined ? { body: JSON.stringify(options.body) } : options.form ? { body: options.form } : {}),
      ...(options.signal ? { signal: options.signal } : {}),
    });
  } catch (err) {
    if (options.signal?.aborted) throw err;
    // A network-level failure locally almost always means the Functions host
    // is not running, which is worth saying rather than making someone guess.
    throw new ApiError(`Could not reach ${API_BASE}${path}. Is the API running? Try: npm run dev:api`);
  }

  if (!response.ok) {
    let body: unknown;
    try {
      body = await response.json();
    } catch {
      body = undefined;
    }
    const { message, code } = describeFailure(path, response.status, body);
    throw new ApiError(message, response.status, code);
  }

  return (await response.json()) as T;
}

/**
 * A request whose answer is a file, not JSON (the quote document, D-233): the same token, the same
 * failures read the same way, the body as a Blob for the page to open or save.
 */
async function requestBlob(path: string, signal?: AbortSignal): Promise<Blob> {
  const token = await currentAccessToken();
  let response: Response;
  try {
    response = await fetch(`${API_BASE}${path}`, { headers: authorizationHeaders(token), ...(signal ? { signal } : {}) });
  } catch (err) {
    if (signal?.aborted) throw err;
    throw new ApiError(`Could not reach ${API_BASE}${path}. Is the API running? Try: npm run dev:api`);
  }
  if (!response.ok) {
    let body: unknown;
    try {
      body = await response.json();
    } catch {
      body = undefined;
    }
    const { message, code } = describeFailure(path, response.status, body);
    throw new ApiError(message, response.status, code);
  }
  return response.blob();
}

/** Anonymous: the API's release version, for the version line in the side menu. */
export const fetchHealth = (signal?: AbortSignal): Promise<HealthResponse> =>
  request<HealthResponse>('/health', { ...(signal ? { signal } : {}) });

export const fetchMe = (signal?: AbortSignal): Promise<MeResponse> =>
  request<MeResponse>('/me', { ...(signal ? { signal } : {}) });

// `/users`, not `/admin/users`: the Functions host reserves `admin/` for
// itself. The SPA's own /admin/… routes are unaffected — different server.
export const fetchAdminUsers = (signal?: AbortSignal): Promise<AdminUsersResponse> =>
  request<AdminUsersResponse>('/users', { ...(signal ? { signal } : {}) });

export const putUserRoles = (userId: string, roles: readonly Role[]): Promise<SetRolesResponse> => {
  const body: SetRolesRequest = { roles };
  return request<SetRolesResponse>(`/users/${encodeURIComponent(userId)}/roles`, { method: 'PUT', body });
};

export const fetchPersonLinks = (signal?: AbortSignal): Promise<PersonLinksResponse> =>
  request<PersonLinksResponse>('/users/jobber-links', { ...(signal ? { signal } : {}) });

/** Link a sign-in to a Jobber user; `null` unlinks. */
export const putPersonLink = (userId: string, jobberUserId: string | null): Promise<SetPersonLinkResponse> => {
  const body: SetPersonLinkRequest = { jobberUserId };
  return request<SetPersonLinkResponse>(`/users/${encodeURIComponent(userId)}/jobber-user`, { method: 'PUT', body });
};

/** The sign-in for a Jobber user who has not signed in yet, made (staff, linked) or found. */
export const createAccountFromJobber = (jobberUserId: string): Promise<AccountFromJobberResponse> => {
  const body: AccountFromJobberRequest = { jobberUserId };
  return request<AccountFromJobberResponse>('/users/from-jobber', { method: 'POST', body });
};

/** Every sign-in's notification lines that are on, and the lines a person can have (design 32). */
export const fetchUsersNotifications = (signal?: AbortSignal): Promise<UsersNotificationsResponse> =>
  request<UsersNotificationsResponse>('/users/notifications', { ...(signal ? { signal } : {}) });

/** Settings → Accounts' *Project Manager in Jobber* column (design 46 §8): Jobber's Project Manager options and who each matched one means. */
export const fetchProjectManagerOptions = (signal?: AbortSignal): Promise<JobberOptionPeopleResponse> =>
  request<JobberOptionPeopleResponse>('/users/project-manager-options', { ...(signal ? { signal } : {}) });

/** The option a person is, or null for none; answers the column. 409 `option_taken` when another person has it. */
export const putProjectManagerOption = (userId: string, option: string | null): Promise<JobberOptionPeopleResponse> => {
  const body: SetJobberOptionBody = { option };
  return request<JobberOptionPeopleResponse>(`/users/${encodeURIComponent(userId)}/project-manager-option`, { method: 'PUT', body });
};

/** Settings → Accounts' *AM in Jobber* column (design 46 §14): Jobber's Account Manager names, those no longer offered that open things hold, and who each matched one means. */
export const fetchAccountManagerOptions = (signal?: AbortSignal): Promise<JobberOptionPeopleResponse> =>
  request<JobberOptionPeopleResponse>('/users/account-manager-options', { ...(signal ? { signal } : {}) });

/** The Account Manager name a person is, or null for none; answers the column. 409 `option_taken` when another person has it. */
export const putAccountManagerOption = (userId: string, option: string | null): Promise<JobberOptionPeopleResponse> => {
  const body: SetJobberOptionBody = { option };
  return request<JobberOptionPeopleResponse>(`/users/${encodeURIComponent(userId)}/account-manager-option`, { method: 'PUT', body });
};

/** A person's notification lines, as the whole set. */
export const putUserNotifications = (userId: string, kinds: readonly string[]): Promise<SetUserNotificationsResponse> => {
  const body: SetUserNotificationsRequest = { kinds };
  return request<SetUserNotificationsResponse>(`/users/${encodeURIComponent(userId)}/notifications`, { method: 'PUT', body });
};

/** Settings → Notifications: the lines, who each is on for, paused, last sent. */
export const fetchNotifications = (signal?: AbortSignal): Promise<NotificationsResponse> =>
  request<NotificationsResponse>('/notifications', { ...(signal ? { signal } : {}) });

/** Pause or resume a line for everyone; answers the lines. */
export const putNotificationPaused = (kind: string, paused: boolean): Promise<NotificationsResponse> => {
  const body: SetNotificationPausedRequest = { paused };
  return request<NotificationsResponse>(`/notifications/${encodeURIComponent(kind)}/paused`, { method: 'PUT', body });
};

/** The Ops Tracker's newest rows (design 45). */
export const fetchOpsTrackerRows = (signal?: AbortSignal): Promise<OpsTrackerRowsResponse> =>
  request<OpsTrackerRowsResponse>('/ops-tracker/rows', { ...(signal ? { signal } : {}) });

/** A failed Ops Tracker row tried again: answers the rows. */
export const retryOpsTrackerRow = (id: number): Promise<OpsTrackerRowsResponse> =>
  request<OpsTrackerRowsResponse>(`/ops-tracker/rows/${id}/retry`, { method: 'POST' });

/** An address line's recipients, the whole set (design 45): answers the lines. */
export const putNotificationRecipients = (kind: string, body: SetNotificationRecipientsRequest): Promise<NotificationsResponse> =>
  request<NotificationsResponse>(`/notifications/${encodeURIComponent(kind)}/recipients`, { method: 'PUT', body });

/** What was sent, newest first: a line, a person, a page. */
export const fetchNotificationDeliveries = (query: { readonly kind?: string; readonly user?: string; readonly offset: number; readonly limit: number }, signal?: AbortSignal): Promise<NotificationDeliveriesResponse> => {
  const params = new URLSearchParams();
  if (query.kind) params.set('kind', query.kind);
  if (query.user) params.set('user', query.user);
  params.set('offset', String(query.offset));
  params.set('limit', String(query.limit));
  return request<NotificationDeliveriesResponse>(`/notifications/deliveries?${params.toString()}`, { ...(signal ? { signal } : {}) });
};

/**
 * Sign-in, step one (D-066): ask for a six-digit code to this address.
 * Anonymous. The answer is the same whether or not the address can sign in —
 * a code arrives only if it can.
 */
export const requestSignInCode = (email: string): Promise<OkResponse> =>
  request<OkResponse>('/sign-in/code', { method: 'POST', body: { email } });

export const fetchVisitRequestContext = (itemId: string, jobNumber: number | null, signal?: AbortSignal): Promise<VisitRequestContextResponse> => {
  const q = new URLSearchParams({ itemId });
  if (jobNumber !== null) q.set('jobNumber', String(jobNumber));
  return request<VisitRequestContextResponse>(`/visit-requests/context?${q.toString()}`, { ...(signal ? { signal } : {}) });
};

export const createVisitRequest = (body: CreateVisitRequestBody): Promise<CreateVisitRequestResponse> =>
  request<CreateVisitRequestResponse>('/visit-requests', { method: 'POST', body });

export const fetchVisitRequest = (id: number, signal?: AbortSignal): Promise<VisitRequest> =>
  request<VisitRequest>(`/visit-requests/${id}`, { ...(signal ? { signal } : {}) });

export const fetchJobberCommands = (signal?: AbortSignal): Promise<JobberCommandsResponse> =>
  request<JobberCommandsResponse>('/jobber/commands', { ...(signal ? { signal } : {}) });

// The job work-item tracker (design 13): the "Work items" tab, its items and its dates.
// To-dos replace checklists (design 39, D-228): the defaults in Settings → To-dos, a job's and a quote's lists, and the writes on one to-do the checklist row had.
export const fetchTodoDefaults = (signal?: AbortSignal): Promise<TodoDefaultsResponse> =>
  request<TodoDefaultsResponse>('/todo-defaults', signal ? { signal } : {});
export const createTodoDefault = (body: CreateTodoDefaultBody): Promise<TodoDefault> =>
  request<TodoDefault>('/todo-defaults', { method: 'POST', body });
export const updateTodoDefault = (id: number, body: UpdateTodoDefaultBody): Promise<TodoDefault> =>
  request<TodoDefault>(`/todo-defaults/${id}`, { method: 'PUT', body });
export const orderTodoDefaults = (body: OrderTodoDefaultsBody): Promise<OkResponse> =>
  request<OkResponse>('/todo-defaults/order', { method: 'PUT', body });
export const fetchJobTodos = (jobNumber: number, signal?: AbortSignal): Promise<JobTodosResponse> =>
  request<JobTodosResponse>(`/jobs/${jobNumber}/to-dos`, signal ? { signal } : {});
export const fetchQuoteTodos = (quoteNumber: string, signal?: AbortSignal): Promise<QuoteTodosResponse> =>
  request<QuoteTodosResponse>(`/quotes/${quoteNumber}/to-dos`, signal ? { signal } : {});
/** A PO's one list (design 46, D-289): read when its row opens, and again after every write on it. */
export const fetchPurchaseOrderTodos = (id: number, signal?: AbortSignal): Promise<PurchaseOrderTodosResponse> =>
  request<PurchaseOrderTodosResponse>(`/purchase-orders/${id}/to-dos`, signal ? { signal } : {});
export const addTodoLink = (todoId: number, body: AddTodoLinkBody): Promise<TodoLink> =>
  request<TodoLink>(`/to-dos/${todoId}/links`, { method: 'POST', body });
export const removeTodoLink = (todoId: number, linkId: number): Promise<OkResponse> =>
  request<OkResponse>(`/to-dos/${todoId}/links/${linkId}`, { method: 'DELETE' });
export const setTodoProof = (todoId: number, body: SetTodoProofBody): Promise<OkResponse> =>
  request<OkResponse>(`/to-dos/${todoId}/proof`, { method: 'PUT', body });
export const removeTodo = (todoId: number): Promise<OkResponse> =>
  request<OkResponse>(`/to-dos/${todoId}`, { method: 'DELETE' });
// File types (design 28, D-151): the list any sign-in reads, the admin's writes, and a stored file's type set.
export const fetchFileTypes = (signal?: AbortSignal): Promise<FileTypesResponse> =>
  request<FileTypesResponse>('/file-types', signal ? { signal } : {});
export const createFileType = (body: CreateFileTypeBody): Promise<FileType> =>
  request<FileType>('/file-types', { method: 'POST', body });
export const updateFileType = (id: number, body: UpdateFileTypeBody): Promise<FileType> =>
  request<FileType>(`/file-types/${id}`, { method: 'PUT', body });
export const orderFileTypes = (body: OrderFileTypesBody): Promise<OkResponse> =>
  request<OkResponse>('/file-types/order', { method: 'PUT', body });
export const setFileType = (fileId: string, body: SetFileTypeBody): Promise<AttachedFileResponse> =>
  request<AttachedFileResponse>(`/files/${fileId}/type`, { method: 'PUT', body });
// The picker (design 28, slice 2): the files already in UnitedOS, searched and scoped, and the links Choose existing makes.
export const searchFiles = (query: URLSearchParams, signal?: AbortSignal): Promise<FileSearchResponse> => {
  const qs = query.toString();
  return request<FileSearchResponse>(`/files${qs ? `?${qs}` : ''}`, signal ? { signal } : {});
};
export const linkFiles = (body: LinkFilesBody): Promise<LinkFilesResponse> =>
  request<LinkFilesResponse>('/file-links', { method: 'POST', body });
// The schedule of values (design 14, D-078).
export const fetchJobSov = (jobNumber: number, signal?: AbortSignal): Promise<JobSovResponse> =>
  request<JobSovResponse>(`/jobs/${jobNumber}/schedule-of-values`, signal ? { signal } : {});
export const createSovItem = (jobNumber: number, body: SaveSovItemBody): Promise<SovItem> =>
  request<SovItem>(`/jobs/${jobNumber}/schedule-of-values`, { method: 'POST', body });
export const saveSovItem = (jobNumber: number, id: number, body: SaveSovItemBody): Promise<SovItem> =>
  request<SovItem>(`/jobs/${jobNumber}/schedule-of-values/${id}`, { method: 'PUT', body });
export const deleteSovItem = (jobNumber: number, id: number): Promise<OkResponse> =>
  request<OkResponse>(`/jobs/${jobNumber}/schedule-of-values/${id}`, { method: 'DELETE' });
/** The history under one row (design 19, slice 3): read on expand, one row at a time. */
export const fetchSovItemHistory = (jobNumber: number, id: number, signal?: AbortSignal): Promise<SovItemHistory> =>
  request<SovItemHistory>(`/jobs/${jobNumber}/schedule-of-values/${id}/history`, signal ? { signal } : {});
/** A labor row's place on The Plan (design 37 §7.7): a work item, Not on the Plan, or neither; answers the whole tab. */
export const setSovPlanLink = (jobNumber: number, id: number, body: SetSovPlanLinkBody): Promise<JobSovResponse> =>
  request<JobSovResponse>(`/jobs/${jobNumber}/schedule-of-values/${id}/plan-link`, { method: 'PUT', body });

// Purchase orders (design 19, slice 2, D-104; lines and receipts by quantity since design 26): the
// Purchase orders page. The list is the rows; the PO whole — lines and receipts — is read on expand,
// and every write answers it whole; a stale version answers 409 `stale_purchase_order`.
/** `filters`: the list's filters and sort as the page's view writes them (`poFilterParams`, design 49); none on the job's tab and the search box. */
export const fetchPurchaseOrders = (view: PurchaseOrderView, q: string, jobNumber: number | null, signal?: AbortSignal, filters?: URLSearchParams): Promise<PurchaseOrdersResponse> => {
  const params = new URLSearchParams({ view });
  if (q.trim()) params.set('q', q.trim());
  if (jobNumber !== null) params.set('jobNumber', String(jobNumber));
  for (const [k, v] of filters ?? []) params.set(k, v);
  return request<PurchaseOrdersResponse>(`/purchase-orders?${params.toString()}`, signal ? { signal } : {});
};
export const fetchPurchaseOrder = (id: number, signal?: AbortSignal): Promise<PurchaseOrderDetail> =>
  request<PurchaseOrderDetail>(`/purchase-orders/${id}`, signal ? { signal } : {});
export const createPurchaseOrder = (body: CreatePurchaseOrderBody): Promise<PurchaseOrderDetail> =>
  request<PurchaseOrderDetail>('/purchase-orders', { method: 'POST', body });
export const savePurchaseOrder = (id: number, body: SavePurchaseOrderBody): Promise<PurchaseOrderDetail> =>
  request<PurchaseOrderDetail>(`/purchase-orders/${id}`, { method: 'PUT', body });
/** Records a receipt; the answer carries the PO whole and the bill-it to-dos the receipt made (design 34, D-200). */
export const receivePurchaseOrder = (id: number, body: ReceiptBody): Promise<ReceiptResponse> =>
  request<ReceiptResponse>(`/purchase-orders/${id}/receipts`, { method: 'POST', body });
export const undoPurchaseOrderReceipt = (id: number, receiptId: number, body: VersionBody): Promise<PurchaseOrderDetail> =>
  request<PurchaseOrderDetail>(`/purchase-orders/${id}/receipts/${receiptId}`, { method: 'DELETE', body });
export const cancelPurchaseOrder = (id: number, body: VersionBody): Promise<PurchaseOrderDetail> =>
  request<PurchaseOrderDetail>(`/purchase-orders/${id}/cancel`, { method: 'POST', body });

// PO drafts (design 50): a PO saved before it is created; every write on the version, a stale one 409 `stale_draft` naming who saved it.
export const fetchPurchaseOrderDrafts = (q: string, jobNumber: number | null, signal?: AbortSignal): Promise<PurchaseOrderDraftsResponse> => {
  const params = new URLSearchParams();
  if (q.trim()) params.set('q', q.trim());
  if (jobNumber !== null) params.set('jobNumber', String(jobNumber));
  const query = params.toString();
  return request<PurchaseOrderDraftsResponse>(`/purchase-order-drafts${query ? `?${query}` : ''}`, signal ? { signal } : {});
};
export const fetchPurchaseOrderDraft = (id: number, signal?: AbortSignal): Promise<PurchaseOrderDraft> =>
  request<PurchaseOrderDraft>(`/purchase-order-drafts/${id}`, signal ? { signal } : {});
/** The first save: the draft, and the to-dos the dialog held as live ones on it. */
export const startPurchaseOrderDraft = (body: StartPurchaseOrderDraftBody): Promise<PurchaseOrderDraft> =>
  request<PurchaseOrderDraft>('/purchase-order-drafts', { method: 'POST', body });
export const savePurchaseOrderDraft = (id: number, body: SavePurchaseOrderDraftBody): Promise<PurchaseOrderDraft> =>
  request<PurchaseOrderDraft>(`/purchase-order-drafts/${id}`, { method: 'PUT', body });
export const deletePurchaseOrderDraft = (id: number, body: VersionBody): Promise<OkResponse> =>
  request<OkResponse>(`/purchase-order-drafts/${id}`, { method: 'DELETE', body });
/** Create PO from a draft: the PO as Create answers it; a refusal keeps the dialog's body on the draft (read it again for its version). */
export const createFromPurchaseOrderDraft = (id: number, body: CreateFromPurchaseOrderDraftBody): Promise<PurchaseOrderDetail> =>
  request<PurchaseOrderDetail>(`/purchase-order-drafts/${id}/create`, { method: 'POST', body });
export const fetchPurchaseOrderDraftTodos = (id: number, signal?: AbortSignal): Promise<PurchaseOrderDraftTodosResponse> =>
  request<PurchaseOrderDraftTodosResponse>(`/purchase-order-drafts/${id}/to-dos`, signal ? { signal } : {});
/** The paid mark on the PO (D-169): the date and the check or ACH number; its undo takes the mark off. The office's note, posted nowhere. */
export const markPurchaseOrderPaid = (id: number, body: PaymentBody): Promise<PurchaseOrderDetail> =>
  request<PurchaseOrderDetail>(`/purchase-orders/${id}/payment`, { method: 'POST', body });
export const undoPurchaseOrderPaid = (id: number, body: VersionBody): Promise<PurchaseOrderDetail> =>
  request<PurchaseOrderDetail>(`/purchase-orders/${id}/payment`, { method: 'DELETE', body });
/** The PO document made now when its latest version has none (design 42): the Email dialog asks before it attaches it. */
export const makePurchaseOrderDocument = (id: number): Promise<PurchaseOrderDetail> =>
  request<PurchaseOrderDetail>(`/purchase-orders/${id}/document`, { method: 'POST' });
/** An internal note on the PO (design 46, D-288): answers the note alone, read back with its writer's name; the PO's version never moves. */
export const addPurchaseOrderNote = (id: number, body: AddPurchaseOrderNoteBody): Promise<PurchaseOrderNote> =>
  request<PurchaseOrderNote>(`/purchase-orders/${id}/notes`, { method: 'POST', body });

/** An internal note's text replaced (2026-11-02); the answer carries who edited it and when. 404 when the note is gone. */
export const editPurchaseOrderNote = (id: number, noteId: number, body: AddPurchaseOrderNoteBody): Promise<PurchaseOrderNote> =>
  request<PurchaseOrderNote>(`/purchase-orders/${id}/notes/${noteId}`, { method: 'PUT', body });

/** An internal note removed — soft on the server (2026-11-02). 404 when it is gone already. */
export const removePurchaseOrderNote = (id: number, noteId: number): Promise<OkResponse> =>
  request<OkResponse>(`/purchase-orders/${id}/notes/${noteId}`, { method: 'DELETE' });

/** Email from the app (design 42): everything the dialog opens with — the sign-in's From, the words, the files. */
export const fetchEmailDraft = (kind: EmailKind, id: string, signal?: AbortSignal): Promise<EmailDraftResponse> =>
  request<EmailDraftResponse>(`/email-drafts?${new URLSearchParams({ kind, id }).toString()}`, signal ? { signal } : {});
/** Send it: written and queued at once; it goes out through the outbox. */
export const sendEmail = (body: SendEmailBody): Promise<SentEmail> =>
  request<SentEmail>('/sent-emails', { method: 'POST', body });
/** A thing's emails, newest first. */
export const fetchSentEmails = (kind: EmailKind, id: string, signal?: AbortSignal): Promise<SentEmailsResponse> =>
  request<SentEmailsResponse>(`/sent-emails?${new URLSearchParams({ kind, id }).toString()}`, signal ? { signal } : {});
// Brex (design 47 §3, D-301): read server-side — the browser never calls Brex. The texted link's reads (`/purchase-orders?record=`,
// D-306) and its Not for a job receipt are any sign-in's; the cost dialog's Retry too; Settings → Brex (status, outbox, Retry) is an admin's.
/** The expense the texted link names: Brex's facts, what the page fills, and the cost already recorded for it. */
export const fetchBrexExpense = (id: string, signal?: AbortSignal): Promise<BrexExpenseResponse> =>
  request<BrexExpenseResponse>(`/brex/expenses/${encodeURIComponent(id)}`, signal ? { signal } : {});
/** The transaction the link names, resolved to its expense and read as the expense; `not_in_brex` until it settles. */
export const fetchBrexTransaction = (id: string, signal?: AbortSignal): Promise<BrexExpenseResponse> =>
  request<BrexExpenseResponse>(`/brex/transactions/${encodeURIComponent(id)}`, signal ? { signal } : {});
/** A Not for a job receipt: the row its file is then uploaded to (kind `brex_receipt`), the receipt queued for Brex. */
export const createBrexReceipt = (body: BrexReceiptBody): Promise<BrexReceiptCreated> =>
  request<BrexReceiptCreated>('/brex/receipts', { method: 'POST', body });
/** A cost's failed Brex writes tried again (the dialog's Retry beside *Brex: failed*); the cost whole. */
export const retryCostBrex = (purchaseOrderId: number): Promise<PurchaseOrderDetail> =>
  request<PurchaseOrderDetail>(`/purchase-orders/${purchaseOrderId}/brex/retry`, { method: 'POST' });
export const fetchBrexStatus = (signal?: AbortSignal): Promise<BrexStatusResponse> =>
  request<BrexStatusResponse>('/brex/status', signal ? { signal } : {});
export const fetchBrexCommands = (signal?: AbortSignal): Promise<BrexCommandsResponse> =>
  request<BrexCommandsResponse>('/brex/commands', signal ? { signal } : {});
export const retryBrexCommand = (id: number): Promise<BrexCommandsResponse> =>
  request<BrexCommandsResponse>(`/brex/commands/${id}/retry`, { method: 'POST' });

/** The vendor spellings used on earlier POs (design 26, slice 3): the dialog's suggestions. */
// The vendor list (D-249): any sign-in reads every vendor (the PO dialog's picker filters it); an admin adds and edits.
export const fetchVendors = (signal?: AbortSignal): Promise<VendorsResponse> =>
  request<VendorsResponse>('/vendors', signal ? { signal } : {});
export const createVendor = (body: CreateVendorBody): Promise<Vendor> =>
  request<Vendor>('/vendors', { method: 'POST', body });
export const updateVendor = (id: number, body: UpdateVendorBody): Promise<Vendor> =>
  request<Vendor>(`/vendors/${id}`, { method: 'PUT', body });

// The app's own settings (design 26): the shop's address a new PO ships to. Any sign-in reads; an admin saves.
export const fetchPurchaseOrderSettings = (signal?: AbortSignal): Promise<PurchaseOrderSettings> =>
  request<PurchaseOrderSettings>('/settings/purchase-orders', signal ? { signal } : {});
export const savePurchaseOrderSettings = (body: SavePurchaseOrderSettingsBody): Promise<PurchaseOrderSettings> =>
  request<PurchaseOrderSettings>('/settings/purchase-orders', { method: 'PUT', body });

// Email templates (design 43): Settings → Emails, an admin's. A reset deletes the kind's row: its default applies again.
export const fetchEmailTemplates = (signal?: AbortSignal): Promise<EmailTemplates> => request<EmailTemplates>('/settings/emails', signal ? { signal } : {});
export const saveEmailTemplate = (kind: string, body: { readonly subject: string; readonly body: string }): Promise<EmailTemplate> =>
  request<EmailTemplate>(`/settings/emails/${encodeURIComponent(kind)}`, { method: 'PUT', body });
export const resetEmailTemplate = (kind: string): Promise<EmailTemplate> => request<EmailTemplate>(`/settings/emails/${encodeURIComponent(kind)}`, { method: 'DELETE' });

// The signed-in person's own profile (design 43): the email signature.
export const fetchProfile = (signal?: AbortSignal): Promise<Profile> => request<Profile>('/profile', signal ? { signal } : {});
export const saveProfile = (body: { readonly signature: string }): Promise<Profile> => request<Profile>('/profile', { method: 'PUT', body });

export const fetchWorkItems = (jobNumber: number, signal?: AbortSignal): Promise<WorkItemTracker> =>
  request<WorkItemTracker>(`/jobs/${jobNumber}/work-items`, signal ? { signal } : {});

export const createWorkItem = (jobNumber: number, body: CreateWorkItemBody): Promise<WorkItem> =>
  request<WorkItem>(`/jobs/${jobNumber}/work-items`, { method: 'POST', body });

export const saveWorkItem = (jobNumber: number, id: number, body: SaveWorkItemBody): Promise<SaveWorkItemResponse> =>
  request<SaveWorkItemResponse>(`/jobs/${jobNumber}/work-items/${id}`, { method: 'PUT', body });

export const deleteWorkItem = (jobNumber: number, id: number): Promise<OkResponse> =>
  request<OkResponse>(`/jobs/${jobNumber}/work-items/${id}`, { method: 'DELETE' });

/** Linked work items (design 37): a new group, its members or head changed, or unlinked. */
export const setWorkItemGroup = (jobNumber: number, body: SetWorkItemGroupBody): Promise<SetWorkItemGroupResponse> =>
  request<SetWorkItemGroupResponse>(`/jobs/${jobNumber}/work-item-groups`, { method: 'PUT', body });

// Work types and their stages (design 36): every write answers the whole list.
export const fetchWorkTypes = (signal?: AbortSignal): Promise<WorkTypesResponse> => request<WorkTypesResponse>('/work-types', signal ? { signal } : {});
export const createWorkType = (body: SaveWorkTypeBody): Promise<WorkTypesResponse> => request<WorkTypesResponse>('/work-types', { method: 'POST', body });
export const saveWorkType = (id: number, body: SaveWorkTypeBody): Promise<WorkTypesResponse> =>
  request<WorkTypesResponse>(`/work-types/${id}`, { method: 'PUT', body });
export const deleteWorkType = (id: number): Promise<WorkTypesResponse> => request<WorkTypesResponse>(`/work-types/${id}`, { method: 'DELETE' });

// A work item's breakdown and the office's corrections to its entries (design 36, slice 2).
export const fetchWorkItemProgress = (jobNumber: number, id: number, signal?: AbortSignal): Promise<WorkItemBreakdown> =>
  request<WorkItemBreakdown>(`/jobs/${jobNumber}/work-items/${id}/progress`, signal ? { signal } : {});
export const correctWorkEntry = (jobNumber: number, id: number, body: CorrectWorkEntryBody): Promise<WorkItemBreakdown> =>
  request<WorkItemBreakdown>(`/jobs/${jobNumber}/work-entries/${id}/correct`, { method: 'POST', body });
/** The split (design 37 §6.5): a head's report placed across its group; answers the head's breakdown. */
export const splitWorkEntry = (jobNumber: number, id: number, body: SplitWorkEntryBody): Promise<WorkItemBreakdown> =>
  request<WorkItemBreakdown>(`/jobs/${jobNumber}/work-entries/${id}/split`, { method: 'POST', body });

// The Notes page (design 36, slice 3): the note is made, its files upload onto it, then submit queues the Jobber note.
export const fetchTechNoteContext = (itemId: string | null, jobNumber: number | null, signal?: AbortSignal): Promise<TechNoteContextResponse> => {
  const q = new URLSearchParams();
  if (itemId !== null) q.set('itemId', itemId);
  if (jobNumber !== null) q.set('jobNumber', String(jobNumber));
  return request<TechNoteContextResponse>(`/tech-notes/context?${q.toString()}`, signal ? { signal } : {});
};
/** The draft its files upload onto (design 45): the same body as the create, its Work done ignored; a repeated client key answers the same draft. */
export const startTechNote = (body: StartTechNoteBody): Promise<CreateTechNoteResponse> => request<CreateTechNoteResponse>('/tech-notes/start', { method: 'POST', body });
export const createTechNote = (body: CreateTechNoteBody): Promise<CreateTechNoteResponse> => request<CreateTechNoteResponse>('/tech-notes', { method: 'POST', body });
export const submitTechNote = (id: number): Promise<SubmitTechNoteResponse> => request<SubmitTechNoteResponse>(`/tech-notes/${id}/submit`, { method: 'POST', body: {} });

/** How many calendar days the plan has, and with a start their dates (design 30). 409 `already_dated` once dated, `has_figures` when fewer days would remove figures unconfirmed. */
export const setWorkDays = (jobNumber: number, body: SetWorkDaysBody): Promise<SetWorkDaysResponse> =>
  request<SetWorkDaysResponse>(`/jobs/${jobNumber}/work-dates`, { method: 'POST', body });

/** Delay project's days after the last one, dated when the plan is. */
export const appendWorkDays = (jobNumber: number, body: AppendWorkDaysBody): Promise<AppendWorkDaysResponse> =>
  request<AppendWorkDaysResponse>(`/jobs/${jobNumber}/work-dates/append`, { method: 'POST', body });

export const setWorkDatesHidden = (jobNumber: number, body: SetWorkDatesHiddenBody): Promise<SetWorkDatesHiddenResponse> =>
  request<SetWorkDatesHiddenResponse>(`/jobs/${jobNumber}/work-dates/hidden`, { method: 'PUT', body });

export const retryJobberCommand = (id: number): Promise<OkResponse> =>
  request<OkResponse>(`/jobber/commands/${id}/retry`, { method: 'POST' });

/** `confirm: false` counts the jobs whose "Click To Schedule" link needs rewriting; `true` queues them. */
export const rewriteScheduleLinks = (confirm: boolean): Promise<ScheduleLinkRewriteResponse> =>
  request<ScheduleLinkRewriteResponse>('/jobber/schedule-links/rewrite', { method: 'POST', body: { confirm } });

/** One job's "Click To Schedule" link, written because an admin asked (D-075): how a test job gets its link while reactions are off. */
export const writeScheduleLink = (jobNumber: number): Promise<ScheduleLinkWriteResponse> =>
  request<ScheduleLinkWriteResponse>(`/jobber/schedule-links/${jobNumber}`, { method: 'POST' });

/** `confirm: false` counts the jobs whose notes link needs rewriting; `true` queues them (design 36, slice 5). */
export const rewriteNotesLinks = (confirm: boolean): Promise<NotesLinkRewriteResponse> =>
  request<NotesLinkRewriteResponse>('/jobber/notes-links/rewrite', { method: 'POST', body: { confirm } });

/** One job's notes link, written because an admin asked: how a test job gets its link while reactions are off. */
export const writeNotesLink = (jobNumber: number): Promise<NotesLinkWriteResponse> =>
  request<NotesLinkWriteResponse>(`/jobber/notes-links/${jobNumber}`, { method: 'POST' });

export const clearShadowWrites = (): Promise<ShadowClearResponse> =>
  request<ShadowClearResponse>('/jobber/commands/shadow/clear', { method: 'POST' });

export const fetchJobberStatus = (signal?: AbortSignal): Promise<JobberStatusResponse> =>
  request<JobberStatusResponse>('/jobber/status', { ...(signal ? { signal } : {}) });

export const startJobberConnect = (returnTo: string, purpose: JobberConnectionPurpose): Promise<JobberConnectResponse> => {
  const body: JobberConnectRequest = { returnTo, purpose };
  return request<JobberConnectResponse>('/jobber/connect', { method: 'POST', body });
};

export const testJobber = (purpose: JobberConnectionPurpose): Promise<JobberTestResponse> => {
  const body: JobberPurposeRequest = { purpose };
  return request<JobberTestResponse>('/jobber/test', { method: 'POST', body });
};

export const disconnectJobber = (purpose: JobberConnectionPurpose): Promise<JobberDisconnectResponse> => {
  const body: JobberPurposeRequest = { purpose };
  return request<JobberDisconnectResponse>('/jobber/disconnect', { method: 'POST', body });
};

export const pullJobberNow = (): Promise<PullResponse> => request<PullResponse>('/jobber/pull', { method: 'POST' });

/** The file-storage probe (FileStorageTasks.md, Phase 0): an admin read whose answer is shown as it came; temporary. */
export const runFilesProbe = (): Promise<unknown> => request<unknown>('/files/probe');

// The file fetcher (D-099, contexts/20): Settings → Jobber → Files.
export const fetchFilesFetchStatus = (signal?: AbortSignal): Promise<FileFetchStatusResponse> =>
  request<FileFetchStatusResponse>('/files/fetch', signal ? { signal } : {});
export const retryFileFetches = (body: FileFetchRetryBody): Promise<FileFetchActionResponse> =>
  request<FileFetchActionResponse>('/files/fetch/retry', { method: 'POST', body });
export const runFileFetchNow = (): Promise<FileFetchActionResponse> => request<FileFetchActionResponse>('/files/fetch/run', { method: 'POST', body: {} });

// Conversion (design 45 §15.2): every uploaded video an MP4, every HEIC a JPEG — Settings → Jobber → Files.
export const fetchFileConversions = (signal?: AbortSignal): Promise<FileConversionsResponse> =>
  request<FileConversionsResponse>('/files/conversions', signal ? { signal } : {});
/** A failed or skipped conversion tried again; the list as it is now. */
export const retryFileConversion = (fileId: string): Promise<FileConversionsResponse> =>
  request<FileConversionsResponse>(`/files/conversions/${encodeURIComponent(fileId)}/retry`, { method: 'POST' });

export const fetchBackfillStatus = (signal?: AbortSignal): Promise<BackfillStatusResponse> =>
  request<BackfillStatusResponse>('/jobber/backfill', { ...(signal ? { signal } : {}) });

/** Re-apply the raw archive to the mirror, parents first. 409 'rebuild_in_progress' while one runs. */
export const rebuildMirror = (): Promise<{ ok: boolean }> => request<{ ok: boolean }>('/jobber/mirror/rebuild', { method: 'POST', body: {} });

/** `start` names the datasets; `rewalk` names one (`entity` + `pass`); `pause` and `resume` name none. */
export const backfillAction = (action: BackfillActionRequest['action'], target: Omit<BackfillActionRequest, 'action'> = {}): Promise<BackfillStatusResponse> => {
  const body: BackfillActionRequest = { action, ...target };
  return request<BackfillStatusResponse>('/jobber/backfill', { method: 'POST', body });
};

export const fetchJobberRuns = (signal?: AbortSignal): Promise<PullResponse> =>
  request<PullResponse>('/jobber/runs', { ...(signal ? { signal } : {}) });

export const fetchJobberWebhooks = (filter: { status?: string; topic?: string; itemId?: string; beforeId?: number; limit?: number }, signal?: AbortSignal): Promise<WebhooksResponse> => {
  const q = new URLSearchParams();
  if (filter.status) q.set('status', filter.status);
  if (filter.topic) q.set('topic', filter.topic);
  if (filter.itemId) q.set('itemId', filter.itemId);
  if (filter.beforeId) q.set('beforeId', String(filter.beforeId));
  if (filter.limit) q.set('limit', String(filter.limit));
  const qs = q.toString();
  return request<WebhooksResponse>(`/jobber/webhooks${qs ? `?${qs}` : ''}`, { ...(signal ? { signal } : {}) });
};

export const fetchJobberWebhookPayload = (receiptId: number, signal?: AbortSignal): Promise<WebhookPayloadResponse> =>
  request<WebhookPayloadResponse>(`/jobber/webhooks/${receiptId}/payload`, { ...(signal ? { signal } : {}) });

export const fetchJobberRefetches = (state: 'pending' | 'dead' | 'done', signal?: AbortSignal): Promise<RefetchesResponse> =>
  request<RefetchesResponse>(`/jobber/refetches?state=${state}`, { ...(signal ? { signal } : {}) });

export const requestJobberRefetch = (itemId: string, kind: string): Promise<void> => {
  const body: RefetchRequest = { itemId, kind };
  return request<OkResponse>('/jobber/refetches', { method: 'POST', body }).then(() => undefined);
};


/** Put people in a band on the calendar (admin; design 18). */
export const setPersonGroup = (personIds: readonly string[], group: CalendarGroup): Promise<OkResponse> => {
  const body: SetGroupRequest = { personIds, group };
  return request<OkResponse>('/people/group', { method: 'PUT', body });
};

/** The working day the calendar's estimates rest on (admin; design 18); answers the saved settings. */
export const putCalendarSettings = (settings: CalendarSettings): Promise<CalendarSettings> =>
  request<CalendarSettings>('/people/calendar', { method: 'PUT', body: settings });

export const setOnSchedule = (personIds: readonly string[], onSchedule: boolean): Promise<OkResponse> => {
  const body: SetOnScheduleRequest = { personIds, onSchedule };
  return request<OkResponse>('/people/on-schedule', { method: 'PUT', body });
};

export const fetchPeople = (signal?: AbortSignal): Promise<PeopleResponse> =>
  request<PeopleResponse>('/people', { ...(signal ? { signal } : {}) });

export const fetchSchedule = (from: Date, to: Date, signal?: AbortSignal): Promise<ScheduleResponse> => {
  const q = new URLSearchParams({ from: from.toISOString(), to: to.toISOString() });
  return request<ScheduleResponse>(`/schedule?${q.toString()}`, { ...(signal ? { signal } : {}) });
};

// ------------------------------------------------------------------ jobs --
// The jobs list and job page (D-060). `query` is the list page's own
// `URLSearchParams`, forwarded to the API unchanged — the URL grammar IS the
// query grammar, so the page never re-encodes it.

export const fetchJobs = (query: URLSearchParams, signal?: AbortSignal): Promise<JobListResponse> => {
  const qs = query.toString();
  return request<JobListResponse>(`/jobs${qs ? `?${qs}` : ''}`, { ...(signal ? { signal } : {}) });
};

export const fetchJobOptions = (signal?: AbortSignal): Promise<JobOptionsResponse> =>
  request<JobOptionsResponse>('/jobs/options', { ...(signal ? { signal } : {}) });

/** The latest notes across every job (design 17), newest first; size 1..50. */
export const fetchLatestNotes = (size: number, signal?: AbortSignal): Promise<NoteStreamResponse> =>
  request<NoteStreamResponse>(`/notes/latest?size=${size}`, { ...(signal ? { signal } : {}) });

export const fetchJobDetail = (jobNumber: number, signal?: AbortSignal): Promise<JobDetail> =>
  request<JobDetail>(`/jobs/${jobNumber}`, { ...(signal ? { signal } : {}) });

export const fetchJobCommunications = (jobNumber: number, signal?: AbortSignal): Promise<JobCommunicationsResponse> =>
  request<JobCommunicationsResponse>(`/jobs/${jobNumber}/communications`, { ...(signal ? { signal } : {}) });

export const fetchJobLineItems = (jobNumber: number, signal?: AbortSignal): Promise<JobLineItemsResponse> =>
  request<JobLineItemsResponse>(`/jobs/${jobNumber}/line-items`, { ...(signal ? { signal } : {}) });

export const fetchJobVisits = (jobNumber: number, signal?: AbortSignal): Promise<JobScheduleResponse> =>
  request<JobScheduleResponse>(`/jobs/${jobNumber}/visits`, { ...(signal ? { signal } : {}) });

export const fetchJobDocuments = (jobNumber: number, signal?: AbortSignal): Promise<JobDocumentsResponse> =>
  request<JobDocumentsResponse>(`/jobs/${jobNumber}/documents`, { ...(signal ? { signal } : {}) });

export const fetchJobLabor = (jobNumber: number, signal?: AbortSignal): Promise<JobLaborResponse> =>
  request<JobLaborResponse>(`/jobs/${jobNumber}/labor`, { ...(signal ? { signal } : {}) });

export const fetchJobFinancials = (jobNumber: number, signal?: AbortSignal): Promise<JobFinancialsResponse> =>
  request<JobFinancialsResponse>(`/jobs/${jobNumber}/financials`, { ...(signal ? { signal } : {}) });

/** The job's Fields card (design 46): its custom fields in the office's order, each with its options and whether it is read-only here. */
export const fetchJobFields = (jobNumber: number, signal?: AbortSignal): Promise<CustomFieldsResponse> =>
  request<CustomFieldsResponse>(`/jobs/${jobNumber}/fields`, { ...(signal ? { signal } : {}) });

/** The Fields card's save and the title's pencil (design 46 §5): only what changed, each with what the person saw. 202 queued, 201 a dry run with writes off; the card back either way. */
export const saveJobFields = (jobNumber: number, body: CustomFieldSaveBody): Promise<CustomFieldsResponse> =>
  request<CustomFieldsResponse>(`/jobs/${jobNumber}/fields`, { method: 'POST', body });

/** A refused save answered on the card (Use Jobber's, Discard): no longer shown to anyone. */
export const markJobFieldSaveSeen = (jobNumber: number, saveId: number): Promise<CustomFieldsResponse> =>
  request<CustomFieldsResponse>(`/jobs/${jobNumber}/field-saves/${saveId}/seen`, { method: 'POST' });

export const fetchJobsColumnsPreference = (signal?: AbortSignal): Promise<PreferenceResponse<string[]>> =>
  request<PreferenceResponse<string[]>>('/preferences/jobs.columns', { ...(signal ? { signal } : {}) });

export const putJobsColumnsPreference = (value: readonly string[]): Promise<PreferenceResponse<string[]>> =>
  request<PreferenceResponse<string[]>>('/preferences/jobs.columns', { method: 'PUT', body: { value } });

// ---------------------------------------------------------------- quotes --
// The quotes list and quote page (design 27): the jobs' pattern — the list page's own
// URLSearchParams forwarded unchanged, one typed function per endpoint.

export const fetchQuotes = (query: URLSearchParams, signal?: AbortSignal): Promise<QuoteListResponse> => {
  const qs = query.toString();
  return request<QuoteListResponse>(`/quotes${qs ? `?${qs}` : ''}`, { ...(signal ? { signal } : {}) });
};

export const fetchQuoteOptions = (signal?: AbortSignal): Promise<QuoteOptionsResponse> =>
  request<QuoteOptionsResponse>('/quotes/options', { ...(signal ? { signal } : {}) });

export const fetchQuoteDetail = (quoteNumber: string, signal?: AbortSignal): Promise<QuoteDetail> =>
  request<QuoteDetail>(`/quotes/${encodeURIComponent(quoteNumber)}`, { ...(signal ? { signal } : {}) });

export const fetchQuoteCommunications = (quoteNumber: string, signal?: AbortSignal): Promise<JobCommunicationsResponse> =>
  request<JobCommunicationsResponse>(`/quotes/${encodeURIComponent(quoteNumber)}/communications`, { ...(signal ? { signal } : {}) });

/** A quote's lines, the editor's read (D-211; every quote since design 38). */
export const fetchQuoteLines = (quoteNumber: string, signal?: AbortSignal): Promise<QuoteLinesResponse> =>
  request<QuoteLinesResponse>(`/quotes/${encodeURIComponent(quoteNumber)}/lines`, { ...(signal ? { signal } : {}) });
/** Save lines to Jobber: the whole list, one command; 202 while it is on its way. */
export const saveQuoteLines = (quoteNumber: string, body: SaveQuoteLinesBody): Promise<QuoteLinesResponse> =>
  request<QuoteLinesResponse>(`/quotes/${encodeURIComponent(quoteNumber)}/lines`, { method: 'PUT', body });

/** The quote's photos note made to match its photos once Save has stored and linked them (design 38, slice 2); a change order's go onto the job at Convert. */
export const syncQuoteLinePhotos = (quoteNumber: string): Promise<QuoteLinePhotosResponse> =>
  request<QuoteLinePhotosResponse>(`/quotes/${encodeURIComponent(quoteNumber)}/line-photos`, { method: 'POST', body: {} });

/** The price list a line is picked from: Jobber's live, visible products and services, the most used first (design 38). */
export const fetchProducts = (signal?: AbortSignal): Promise<ProductsResponse> => request<ProductsResponse>('/products', { ...(signal ? { signal } : {}) });

export const fetchQuoteDocuments = (quoteNumber: string, signal?: AbortSignal): Promise<JobDocumentsResponse> =>
  request<JobDocumentsResponse>(`/quotes/${encodeURIComponent(quoteNumber)}/documents`, { ...(signal ? { signal } : {}) });

/** A quote's Fields card (design 46) — or a draft's, by its ref (`draft-12`): every quote field, Market and Account Manager the draft's own. */
export const fetchQuoteFields = (quoteNumber: string, signal?: AbortSignal): Promise<CustomFieldsResponse> =>
  request<CustomFieldsResponse>(`/quotes/${encodeURIComponent(quoteNumber)}/fields`, { ...(signal ? { signal } : {}) });

/** A quote's Fields card save and title (design 46 §5); a draft's fields are its own (`saveQuoteDraftFields`). */
export const saveQuoteFields = (quoteNumber: string, body: CustomFieldSaveBody): Promise<CustomFieldsResponse> =>
  request<CustomFieldsResponse>(`/quotes/${encodeURIComponent(quoteNumber)}/fields`, { method: 'POST', body });

export const markQuoteFieldSaveSeen = (quoteNumber: string, saveId: number): Promise<CustomFieldsResponse> =>
  request<CustomFieldsResponse>(`/quotes/${encodeURIComponent(quoteNumber)}/field-saves/${saveId}/seen`, { method: 'POST' });

/** The quote as a PDF in Jobber's layout (D-233), made on demand from the mirror: the bytes, for a new tab or a save. */
export const fetchQuoteDocument = (quoteNumber: string, signal?: AbortSignal): Promise<Blob> =>
  requestBlob(`/quotes/${encodeURIComponent(quoteNumber)}/document`, signal);

// Quote writes (design 35): a quote created in Jobber, a quote converted to a job. Each answers the
// request at once (202 while its write runs); the dialog reads it back until it is done.

export const fetchClients = (q: string, signal?: AbortSignal): Promise<ClientsResponse> =>
  request<ClientsResponse>(`/clients?q=${encodeURIComponent(q)}`, { ...(signal ? { signal } : {}) });

export const fetchProperties = (clientId: string, signal?: AbortSignal): Promise<PropertiesResponse> =>
  request<PropertiesResponse>(`/properties?clientId=${encodeURIComponent(clientId)}`, { ...(signal ? { signal } : {}) });

export const fetchQuoteRequestOptions = (signal?: AbortSignal): Promise<QuoteRequestOptionsResponse> =>
  request<QuoteRequestOptionsResponse>('/quote-requests/options', { ...(signal ? { signal } : {}) });

export const createQuoteRequest = (body: CreateQuoteRequestBody): Promise<QuoteCreateRequest> =>
  request<QuoteCreateRequest>('/quote-requests', { method: 'POST', body });

export const fetchQuoteRequest = (id: number, signal?: AbortSignal): Promise<QuoteCreateRequest> =>
  request<QuoteCreateRequest>(`/quote-requests/${id}`, { ...(signal ? { signal } : {}) });

export const convertQuote = (quoteNumber: string, body: ConvertQuoteBody): Promise<QuoteConversion> =>
  request<QuoteConversion>(`/quotes/${encodeURIComponent(quoteNumber)}/conversions`, { method: 'POST', body });

export const fetchQuoteConversion = (id: number, signal?: AbortSignal): Promise<QuoteConversion> =>
  request<QuoteConversion>(`/quote-conversions/${id}`, { ...(signal ? { signal } : {}) });

// Quote drafts (design 44, D-262): + New quote makes a draft UnitedOS keeps; Send to Jobber creates it there
// as Awaiting response, read back through fetchQuoteRequest until it is in the mirror. Every write carries
// the draft's version. Its lines, worksheet, to-dos and files are the quote routes' with `draft-N`.

/** + New quote: the dialog's own body; the draft at once, nothing in Jobber. */
export const createQuoteDraft = (body: CreateQuoteRequestBody): Promise<QuoteDraftCreatedResponse> =>
  request<QuoteDraftCreatedResponse>('/quote-drafts', { method: 'POST', body });
/** Create similar quote (D-266): a draft from `ref` — a quote in Jobber or a draft — with this header, its lines and worksheet copied. */
export const createSimilarQuote = (ref: string, body: CreateQuoteRequestBody): Promise<QuoteDraftCreatedResponse> =>
  request<QuoteDraftCreatedResponse>(`/quotes/${encodeURIComponent(ref)}/similar`, { method: 'POST', body });
/** The drafts being worked, newest first, for the Quotes list. */
export const fetchQuoteDrafts = (signal?: AbortSignal): Promise<QuoteDraftsResponse> =>
  request<QuoteDraftsResponse>('/quote-drafts', { ...(signal ? { signal } : {}) });
export const fetchQuoteDraft = (id: number, signal?: AbortSignal): Promise<QuoteDraft> =>
  request<QuoteDraft>(`/quote-drafts/${id}`, { ...(signal ? { signal } : {}) });
/** General's save: 409 `stale` when the draft moved since it was read. */
export const saveQuoteDraft = (id: number, body: SaveQuoteDraftBody): Promise<QuoteDraftSavedResponse> =>
  request<QuoteDraftSavedResponse>(`/quote-drafts/${id}`, { method: 'PUT', body });
/** The draft's Fields card (design 46 §7): to UnitedOS on the version, never to Jobber; 409 `stale` when the draft moved since it was read. */
export const saveQuoteDraftFields = (id: number, body: QuoteDraftFieldSaveBody): Promise<QuoteDraftSavedResponse> =>
  request<QuoteDraftSavedResponse>(`/quote-drafts/${id}/fields`, { method: 'PUT', body });
/** Discard draft (asked first): soft; its open to-dos close with it. */
export const discardQuoteDraft = (id: number, version: number): Promise<QuoteDraftDiscardedResponse> =>
  request<QuoteDraftDiscardedResponse>(`/quote-drafts/${id}?version=${version}`, { method: 'DELETE' });
/** What Send to Jobber would do, or what stops it, in the order a person fixes them. */
export const fetchQuoteDraftSendCheck = (id: number, signal?: AbortSignal): Promise<QuoteDraftSendCheckResponse> =>
  request<QuoteDraftSendCheckResponse>(`/quote-drafts/${id}/send`, { ...(signal ? { signal } : {}) });
/** Send to Jobber: 202 with the create request; a 409 without an error code is a stop — read the check again. */
export const sendQuoteDraft = (id: number, body: SendQuoteDraftBody): Promise<QuoteDraftSendResponse> =>
  request<QuoteDraftSendResponse>(`/quote-drafts/${id}/send`, { method: 'POST', body });
/** Email on a draft (D-275): the send's body with the draft's version as its token; 202 with the create request, the email held until the quote is made. A 409 without an error code is a stop. */
export const emailQuoteDraft = (id: number, body: SendEmailBody): Promise<QuoteDraftSendResponse> =>
  request<QuoteDraftSendResponse>(`/quote-drafts/${id}/email`, { method: 'POST', body });
/** Preview PDF: the draft as the quote document would print it, marked DRAFT; never stored. */
export const fetchQuoteDraftDocument = (id: number, signal?: AbortSignal): Promise<Blob> =>
  requestBlob(`/quote-drafts/${id}/document`, signal);
/** A draft's lines saved on its version, to UnitedOS only (design 44): the tab as it is now. */
export const saveDraftQuoteLines = (ref: string, body: SaveDraftQuoteLinesBody): Promise<QuoteLinesResponse> =>
  request<QuoteLinesResponse>(`/quotes/${encodeURIComponent(ref)}/lines`, { method: 'PUT', body });
/** The quote's PDF as it prints now, kept as a file on the quote for its email (design 44 §5): the Email dialog asks for it, then reads the draft again. */
export const keepQuoteDocument = (quoteNumber: string): Promise<QuoteDocumentFileResponse> =>
  request<QuoteDocumentFileResponse>(`/quotes/${encodeURIComponent(quoteNumber)}/document-file`, { method: 'POST', body: {} });

export const fetchQuotesColumnsPreference = (signal?: AbortSignal): Promise<PreferenceResponse<string[]>> =>
  request<PreferenceResponse<string[]>>('/preferences/quotes.columns', { ...(signal ? { signal } : {}) });

export const putQuotesColumnsPreference = (value: readonly string[]): Promise<PreferenceResponse<string[]>> =>
  request<PreferenceResponse<string[]>>('/preferences/quotes.columns', { method: 'PUT', body: { value } });

// The quote worksheet (design 27, slice 2; D-144): the whole worksheet read and saved at once, with
// its version; Settings → Quotes the defaults it starts from.
export const fetchQuoteWorksheet = (quoteNumber: string, signal?: AbortSignal): Promise<QuoteWorksheetResponse> =>
  request<QuoteWorksheetResponse>(`/quotes/${encodeURIComponent(quoteNumber)}/worksheet`, { ...(signal ? { signal } : {}) });
export const saveQuoteWorksheet = (quoteNumber: string, body: SaveQuoteWorksheetBody): Promise<QuoteWorksheetResponse> =>
  request<QuoteWorksheetResponse>(`/quotes/${encodeURIComponent(quoteNumber)}/worksheet`, { method: 'PUT', body });
/** The worksheet's import from a PDF (design 30, D-171): a stored upload on the quote read by the AI into candidates; nothing is saved. A fresh call is 10–60 seconds; an earlier read is reused unless `fresh` (D-172). */
export const importQuoteWorksheet = (quoteNumber: string, body: ImportQuoteWorksheetBody, signal?: AbortSignal): Promise<QuoteImportResponse> =>
  request<QuoteImportResponse>(`/quotes/${encodeURIComponent(quoteNumber)}/worksheet/import`, { method: 'POST', body, ...(signal ? { signal } : {}) });
/** The quote's earlier reads (D-172): the newest of each PDF still on the quote, so Import reopens on what was found. */
export const fetchQuoteImports = (quoteNumber: string, signal?: AbortSignal): Promise<QuoteImportReadsResponse> =>
  request<QuoteImportReadsResponse>(`/quotes/${encodeURIComponent(quoteNumber)}/worksheet/import`, signal ? { signal } : {});
export const fetchQuoteSettings = (signal?: AbortSignal): Promise<QuoteSettings> => request<QuoteSettings>('/settings/quotes', signal ? { signal } : {});
/** A purchase order's lines from a vendor's quote (design 41): a stored file the person may open, read by the AI into a header and lines; nothing is saved. An earlier read is reused unless `fresh` (D-172). */
export const importPurchaseOrderLines = (body: ImportPurchaseOrderBody, signal?: AbortSignal): Promise<PurchaseOrderImportResponse> =>
  request<PurchaseOrderImportResponse>('/purchase-orders/import', { method: 'POST', body, ...(signal ? { signal } : {}) });
/** The same read of a PDF still in the browser (D-235): a new PO has nothing to attach a file to until Create, so the bytes go with the question and the file waits to be attached. */
export const importPurchaseOrderFile = (file: File, jobNumber: number | null, signal?: AbortSignal): Promise<PurchaseOrderImportResponse> => {
  const form = new FormData();
  form.append('file', file, file.name);
  if (jobNumber !== null) form.append('jobNumber', String(jobNumber));
  return request<PurchaseOrderImportResponse>('/purchase-orders/import/file', { method: 'POST', form, ...(signal ? { signal } : {}) });
};
/** The job's earlier reads (design 41, D-172) and its Jobber id: the newest of each PDF the job lists, so the picker says what each was found to hold. */
export const fetchPurchaseOrderImports = (jobNumber: number, signal?: AbortSignal): Promise<PurchaseOrderImportReadsResponse> =>
  request<PurchaseOrderImportReadsResponse>(`/purchase-orders/import?${new URLSearchParams({ job: String(jobNumber) })}`, signal ? { signal } : {});
export const saveQuoteSettings = (body: SaveQuoteSettingsBody): Promise<QuoteSettings> => request<QuoteSettings>('/settings/quotes', { method: 'PUT', body });

/** Whether the rail is collapsed to icons (D-084); null until the person has ever toggled it. */
export const fetchNavCollapsedPreference = (signal?: AbortSignal): Promise<PreferenceResponse<boolean>> =>
  request<PreferenceResponse<boolean>>('/preferences/nav.collapsed', { ...(signal ? { signal } : {}) });

export const putNavCollapsedPreference = (value: boolean): Promise<PreferenceResponse<boolean>> =>
  request<PreferenceResponse<boolean>>('/preferences/nav.collapsed', { method: 'PUT', body: { value } });

/** Whether the job page's notes panel is open (D-083); null until the person has ever toggled it. */
export const fetchNotesOpenPreference = (signal?: AbortSignal): Promise<PreferenceResponse<boolean>> =>
  request<PreferenceResponse<boolean>>('/preferences/job.notes_open', { ...(signal ? { signal } : {}) });

export const putNotesOpenPreference = (value: boolean): Promise<PreferenceResponse<boolean>> =>
  request<PreferenceResponse<boolean>>('/preferences/job.notes_open', { method: 'PUT', body: { value } });

/** The notes panel's width in px (D-085), 260–640; null until the person has dragged it. */
export const fetchNotesWidthPreference = (signal?: AbortSignal): Promise<PreferenceResponse<number>> =>
  request<PreferenceResponse<number>>('/preferences/job.notes_width', { ...(signal ? { signal } : {}) });

export const putNotesWidthPreference = (value: number): Promise<PreferenceResponse<number>> =>
  request<PreferenceResponse<number>>('/preferences/job.notes_width', { method: 'PUT', body: { value } });

/** The schedule view last chosen (day … capacity; `SCHEDULE_VIEWS`); null until the person has ever picked one. */
export const fetchScheduleViewPreference = (signal?: AbortSignal): Promise<PreferenceResponse<string>> =>
  request<PreferenceResponse<string>>('/preferences/schedule.view', { ...(signal ? { signal } : {}) });

export const putScheduleViewPreference = (value: string): Promise<PreferenceResponse<string>> =>
  request<PreferenceResponse<string>>('/preferences/schedule.view', { method: 'PUT', body: { value } });

/** The screen's theme (light · dark · system — `THEME_CHOICES`); null until the person has ever picked one. */
export const fetchThemePreference = (signal?: AbortSignal): Promise<PreferenceResponse<string>> =>
  request<PreferenceResponse<string>>('/preferences/ui.theme', { ...(signal ? { signal } : {}) });

export const putThemePreference = (value: ThemeChoice): Promise<PreferenceResponse<string>> =>
  request<PreferenceResponse<string>>('/preferences/ui.theme', { method: 'PUT', body: { value } });

/** A meeting's Issues / To-dos tab shows only the person's own (MeetingsTasks §8), the same in every meeting; null until the person has ever ticked it. */
export const fetchMeetingsMinePreference = (tab: 'issues' | 'todos', signal?: AbortSignal): Promise<PreferenceResponse<boolean>> =>
  request<PreferenceResponse<boolean>>(`/preferences/meetings.${tab}_mine`, { ...(signal ? { signal } : {}) });

export const putMeetingsMinePreference = (tab: 'issues' | 'todos', value: boolean): Promise<PreferenceResponse<boolean>> =>
  request<PreferenceResponse<boolean>>(`/preferences/meetings.${tab}_mine`, { method: 'PUT', body: { value } });

// ------------------------------------------------------------- meetings --
// Meetings, slice 1 (design 19, D-100). Every meeting is listed and opens for everyone (D-102);
// what a meeting holds answers 404 to a person not in it.

export const fetchMeetings = (signal?: AbortSignal): Promise<MeetingListResponse> =>
  request<MeetingListResponse>('/meetings', signal ? { signal } : {});
export const createMeeting = (body: CreateMeetingBody): Promise<MeetingResponse> =>
  request<MeetingResponse>('/meetings', { method: 'POST', body });
export const fetchMeeting = (id: number, signal?: AbortSignal): Promise<MeetingResponse> =>
  request<MeetingResponse>(`/meetings/${id}`, signal ? { signal } : {});
export const saveMeeting = (id: number, body: SaveMeetingBody): Promise<MeetingResponse> =>
  request<MeetingResponse>(`/meetings/${id}`, { method: 'PUT', body });
export const removeMeeting = (id: number): Promise<OkResponse> =>
  request<OkResponse>(`/meetings/${id}`, { method: 'DELETE' });
export const setMeetingAttendees = (id: number, body: SetAttendeesBody): Promise<OkResponse> =>
  request<OkResponse>(`/meetings/${id}/attendees`, { method: 'PUT', body });
export const fetchMeetingIssues = (id: number, signal?: AbortSignal): Promise<IssueListResponse> =>
  request<IssueListResponse>(`/meetings/${id}/issues`, signal ? { signal } : {});
export const fetchMeetingTodos = (id: number, signal?: AbortSignal): Promise<MeetingTodosResponse> =>
  request<MeetingTodosResponse>(`/meetings/${id}/to-dos`, signal ? { signal } : {});
export const fetchMeetingHeadlines = (id: number, signal?: AbortSignal): Promise<HeadlineListResponse> =>
  request<HeadlineListResponse>(`/meetings/${id}/headlines`, signal ? { signal } : {});
/** The Scorecard's live weeks (D-188): the twelve before `asOf`'s week — the meeting's day, or today before it. */
export const fetchMeetingScorecard = (id: number, asOf: string, signal?: AbortSignal): Promise<ScorecardResponse> =>
  request<ScorecardResponse>(`/meetings/${id}/scorecard?${new URLSearchParams({ asOf }).toString()}`, signal ? { signal } : {});
export const createHeadline = (meetingId: number, body: CreateHeadlineBody): Promise<HeadlineResponse> =>
  request<HeadlineResponse>(`/meetings/${meetingId}/headlines`, { method: 'POST', body });
export const saveHeadline = (id: number, body: SaveHeadlineBody): Promise<HeadlineResponse> =>
  request<HeadlineResponse>(`/headlines/${id}`, { method: 'PUT', body });
export const removeHeadline = (id: number): Promise<OkResponse> =>
  request<OkResponse>(`/headlines/${id}`, { method: 'DELETE' });
/** "Add to IDS": the issue made from a headline, in its meeting. */
export const headlineToIssue = (id: number): Promise<IssueResponse> =>
  request<IssueResponse>(`/headlines/${id}/issue`, { method: 'POST', body: {} });
export const createIssue = (body: CreateIssueBody): Promise<IssueResponse> =>
  request<IssueResponse>('/issues', { method: 'POST', body });
export const fetchIssue = (id: number, signal?: AbortSignal): Promise<IssueResponse> =>
  request<IssueResponse>(`/issues/${id}`, signal ? { signal } : {});
export const saveIssue = (id: number, body: SaveIssueBody): Promise<IssueResponse> =>
  request<IssueResponse>(`/issues/${id}`, { method: 'PUT', body });
export const archiveIssue = (id: number): Promise<OkResponse> =>
  request<OkResponse>(`/issues/${id}`, { method: 'DELETE' });
/** Move back to issues (D-185): a long-term issue open again, on a running meeting's Keep Kill Combine too. */
export const moveIssueBackToIssues = (id: number): Promise<OkResponse> =>
  request<OkResponse>(`/issues/${id}/back-to-issues`, { method: 'POST', body: {} });
// To-dos (TodosTasks): one create for every form, one save that may also put a to-do under an issue.
export const createTodo = (body: CreateTodoBody): Promise<TodoResponse> =>
  request<TodoResponse>('/to-dos', { method: 'POST', body });
export const saveTodo = (id: number, body: SaveTodoBody): Promise<TodoResponse> =>
  request<TodoResponse>(`/to-dos/${id}`, { method: 'PUT', body });
/** The To-dos page's Done and Not Done on a to-do in no meeting issue (D-187). */
export const reviewUnlinkedTodo = (id: number, body: ReviewUnlinkedTodoBody): Promise<ReviewUnlinkedTodoResponse> =>
  request<ReviewUnlinkedTodoResponse>(`/to-dos/${id}/review`, { method: 'POST', body });
/** The To-dos page's read (design 39): `status` (open, done, all) and `for` (any, jobs, quotes, none) as the query; the defaults left out. */
export const fetchTodos = (query: URLSearchParams = new URLSearchParams(), signal?: AbortSignal): Promise<TodoListResponse> => {
  const qs = query.toString();
  return request<TodoListResponse>(`/to-dos${qs ? `?${qs}` : ''}`, signal ? { signal } : {});
};
export const fetchOpenIssues = (signal?: AbortSignal): Promise<IssueListResponse> =>
  request<IssueListResponse>('/issues', signal ? { signal } : {});

// Files on issues and to-dos (D-123) and on the job's work (D-132): start → the browser PUTs to storage (lib/uploads.ts) → confirm.
export const startUpload = (body: StartUploadBody): Promise<StartUploadResponse> =>
  request<StartUploadResponse>('/uploads', { method: 'POST', body });
export const confirmUpload = (id: string): Promise<AttachedFileResponse> =>
  request<AttachedFileResponse>(`/uploads/${id}/confirm`, { method: 'POST', body: {} });
/** The files on a target: the meetings' own routes for an issue or a to-do, `attachments` for everything (D-132). */
export const fetchFiles = (kind: AttachKind, id: string, signal?: AbortSignal): Promise<FileListResponse> =>
  request<FileListResponse>(
    kind === 'meeting_issue' ? `/issues/${id}/files` : kind === 'meeting_todo' ? `/to-dos/${id}/files` : `/attachments?${new URLSearchParams({ kind, id })}`,
    signal ? { signal } : {},
  );
/** The file at its address (D-135): as attached, for the lightbox `/files/{id}` opens. */
export const fetchFile = (id: string, signal?: AbortSignal): Promise<AttachedFileResponse> =>
  request<AttachedFileResponse>(`/files/${encodeURIComponent(id)}`, signal ? { signal } : {});
/** A signed link to the file, minutes long: one that shows its viewable copy (design 45 §15.4), or with `download` one that saves the original (the lightbox's Download). */
export const fetchFileLink = (id: string, download = false): Promise<FileLinkResponse> =>
  request<FileLinkResponse>(`/files/${encodeURIComponent(id)}/link${download ? '?download=true' : ''}`);
/** Delete from the picker (D-244): every live link of ours the person may edit closed at once; the file then lists nowhere. */
export const removeFile = (id: string): Promise<{ readonly ok: true }> => request<{ readonly ok: true }>(`/files/${encodeURIComponent(id)}`, { method: 'DELETE' });
export const removeFileLink = (linkId: number): Promise<{ readonly ok: true }> =>
  request<{ readonly ok: true }>(`/file-links/${linkId}`, { method: 'DELETE' });
/** Every sign-in that can use the app, labelled, for the attendee and owner pickers. */
export const fetchAccounts = (signal?: AbortSignal): Promise<AccountsResponse> =>
  request<AccountsResponse>('/accounts', signal ? { signal } : {});

/** The Jobber team for the employee headline's picker (MeetingsTasks §20 #2): ids, names, active. */
export const fetchEmployees = (signal?: AbortSignal): Promise<EmployeesResponse> =>
  request<EmployeesResponse>('/employees', signal ? { signal } : {});

// Running a meeting (design 19, slice 2). Every write answers the occurrence whole.
export const startMeeting = (meetingId: number): Promise<StartMeetingResponse> =>
  request<StartMeetingResponse>(`/meetings/${meetingId}/start`, { method: 'POST', body: {} });
export const fetchPastMeetings = (meetingId: number, signal?: AbortSignal): Promise<PastMeetingsResponse> =>
  request<PastMeetingsResponse>(`/meetings/${meetingId}/past`, signal ? { signal } : {});
/** Previous headlines (MeetingsTasks §6d): this meeting's past occurrences the person was at, newest first. */
export const fetchPreviousHeadlines = (meetingId: number, signal?: AbortSignal): Promise<PreviousHeadlinesResponse> =>
  request<PreviousHeadlinesResponse>(`/meetings/${meetingId}/headlines/previous`, signal ? { signal } : {});
export const fetchLiveMeeting = (id: number, signal?: AbortSignal): Promise<LiveMeetingResponse> =>
  request<LiveMeetingResponse>(`/live-meetings/${id}`, signal ? { signal } : {});
export const fetchLiveRevision = (id: number, signal?: AbortSignal): Promise<RevisionResponse> =>
  request<RevisionResponse>(`/live-meetings/${id}/revision`, signal ? { signal } : {});
export const setLiveSection = (id: number, body: SetSectionBody): Promise<LiveMeetingResponse> =>
  request<LiveMeetingResponse>(`/live-meetings/${id}/section`, { method: 'PUT', body });
export const setLivePaused = (id: number, body: SetPausedBody): Promise<LiveMeetingResponse> =>
  request<LiveMeetingResponse>(`/live-meetings/${id}/pause`, { method: 'PUT', body });
export const takeOverLive = (id: number): Promise<LiveMeetingResponse> =>
  request<LiveMeetingResponse>(`/live-meetings/${id}/driver`, { method: 'PUT', body: {} });
/** The driver ends the meeting; `sendSummary` false completes it without the summary email. */
export const completeLive = (id: number, body: CompleteMeetingBody): Promise<LiveMeetingResponse> =>
  request<LiveMeetingResponse>(`/live-meetings/${id}/complete`, { method: 'POST', body });
/** Anyone's segue card, the sent fields only; any attendee may write it. 404 `no_card` once the card is removed. */
export const saveLiveSegue = (id: number, userId: string, body: SaveSegueBody): Promise<LiveMeetingResponse> =>
  request<LiveMeetingResponse>(`/live-meetings/${id}/segue/${userId}`, { method: 'PUT', body });
/** An empty card for anyone with a sign-in — an attendee whose card was removed, or a guest. */
export const addLiveSegue = (id: number, userId: string): Promise<LiveMeetingResponse> =>
  request<LiveMeetingResponse>(`/live-meetings/${id}/segue/${userId}`, { method: 'POST', body: {} });
export const removeLiveSegue = (id: number, userId: string): Promise<LiveMeetingResponse> =>
  request<LiveMeetingResponse>(`/live-meetings/${id}/segue/${userId}`, { method: 'DELETE' });
/** A reviewed issue back to open in this occurrence (§6b); 409 `bad_outcome` for a combined or moved one. */
export const reopenLive = (id: number, issueId: number): Promise<LiveMeetingResponse> =>
  request<LiveMeetingResponse>(`/live-meetings/${id}/reopen/${issueId}`, { method: 'POST', body: {} });

// Quarterly priorities (MeetingsTasks §10): an issue tagged as one. Every status carries the version read.
export const fetchMeetingPriorities = (meetingId: number, signal?: AbortSignal): Promise<MeetingPrioritiesResponse> =>
  request<MeetingPrioritiesResponse>(`/meetings/${meetingId}/priorities`, signal ? { signal } : {});
/** Mark as Quarterly Priority; `liveMeetingId` when marked in the room. Answers the issue with its priority; a person may hold several (MeetingsTasks §14). */
export const makePriority = (issueId: number, body: MakePriorityBody): Promise<IssueResponse> =>
  request<IssueResponse>(`/issues/${issueId}/priority`, { method: 'POST', body });
export const savePriority = (issueId: number, body: SavePriorityBody): Promise<QuarterlyPriorityResponse> =>
  request<QuarterlyPriorityResponse>(`/priorities/${issueId}`, { method: 'PUT', body });
/** A status outside a meeting (the Priorities tab, the issue page). */
export const setPriorityStatus = (issueId: number, body: PriorityStatusBody): Promise<QuarterlyPriorityResponse> =>
  request<QuarterlyPriorityResponse>(`/priorities/${issueId}/status`, { method: 'PUT', body });
/** A status in the room: this occurrence's report. */
export const setLivePriorityStatus = (id: number, issueId: number, body: PriorityStatusBody): Promise<LiveMeetingResponse> =>
  request<LiveMeetingResponse>(`/live-meetings/${id}/priorities/${issueId}/status`, { method: 'PUT', body });
/** Off Track's Add to IDS: the priority's one linked issue in this meeting. */
export const livePriorityToIds = (id: number, issueId: number): Promise<LiveMeetingResponse> =>
  request<LiveMeetingResponse>(`/live-meetings/${id}/priorities/${issueId}/ids`, { method: 'POST', body: {} });
export const fetchIssueHistory = (id: number, signal?: AbortSignal): Promise<IssueHistoryResponse> =>
  request<IssueHistoryResponse>(`/issues/${id}/history`, signal ? { signal } : {});
/** Related To-Do's (MeetingsTasks §8): every other to-do on the same issue, closed ones included. */
export const fetchRelatedTodos = (id: number, signal?: AbortSignal): Promise<TodoListResponse> =>
  request<TodoListResponse>(`/to-dos/${id}/related`, signal ? { signal } : {});
export const fetchTodoHistory = (id: number, signal?: AbortSignal): Promise<TodoHistoryResponse> =>
  request<TodoHistoryResponse>(`/to-dos/${id}/history`, signal ? { signal } : {});

// The toolkit import and its legacy meetings (D-108).
export const importToolkitMeetings = (body: MeetingImportBody): Promise<MeetingImportResponse> =>
  request<MeetingImportResponse>('/meeting-imports', { method: 'POST', body });
export const fetchLegacyMeetings = (meetingId: number, signal?: AbortSignal): Promise<LegacyMeetingsResponse> =>
  request<LegacyMeetingsResponse>(`/meetings/${meetingId}/legacy`, signal ? { signal } : {});
export const fetchLegacyMeeting = (id: number, signal?: AbortSignal): Promise<LegacyMeetingResponse> =>
  request<LegacyMeetingResponse>(`/legacy-meetings/${id}`, signal ? { signal } : {});
export const saveLiveNote = (id: number, section: AgendaSection, body: SaveNoteBody): Promise<LiveMeetingResponse> =>
  request<LiveMeetingResponse>(`/live-meetings/${id}/notes/${section}`, { method: 'PUT', body });
export const saveLiveRound = (id: number, issueId: number, body: SaveRoundBody): Promise<LiveMeetingResponse> =>
  request<LiveMeetingResponse>(`/live-meetings/${id}/rounds/${issueId}`, { method: 'PUT', body });
export const liveOutcome = (id: number, issueId: number, body: OutcomeBody): Promise<LiveMeetingResponse> =>
  request<LiveMeetingResponse>(`/live-meetings/${id}/outcomes/${issueId}`, { method: 'POST', body });
/** Combine, one call (D-121): the survivor re-made as the room edited it, the others folded in and their open to-dos moved across. 400 `bad_merge`, `bad_meetings`, `no_meetings`. */
export const combineLiveIssues = (id: number, body: CombineIssuesBody): Promise<LiveMeetingResponse> =>
  request<LiveMeetingResponse>(`/live-meetings/${id}/combine`, { method: 'POST', body });
export const liveReview = (id: number, todoId: number, body: ReviewTodoBody): Promise<LiveMeetingResponse> =>
  request<LiveMeetingResponse>(`/live-meetings/${id}/reviews/${todoId}`, { method: 'POST', body });

// GC billing (design 20, slice 1, D-113): the list that replaces the GC Jobs sheet, a job's Billing
// setup and its note log. The first save (no `expectedVersion`) creates the setup and answers 201; a
// stale version answers 409 `stale_job_billing`.
export const fetchBilling = (view: JobBillingView, q: string, kind: JobBillingKindTab, signal?: AbortSignal): Promise<JobBillingListResponse> => {
  const params = new URLSearchParams();
  if (view !== 'all') params.set('view', view);
  if (kind !== 'all') params.set('kind', kind);
  if (q.trim()) params.set('q', q.trim());
  const query = params.toString();
  return request<JobBillingListResponse>(`/billing${query ? `?${query}` : ''}`, signal ? { signal } : {});
};
export const fetchJobBilling = (jobNumber: number, signal?: AbortSignal): Promise<JobBillingResponse> =>
  request<JobBillingResponse>(`/jobs/${jobNumber}/billing`, signal ? { signal } : {});
export const saveJobBilling = (jobNumber: number, body: SaveJobBillingBody): Promise<JobBillingResponse> =>
  request<JobBillingResponse>(`/jobs/${jobNumber}/billing`, { method: 'PUT', body });
export const addJobBillingNote = (jobNumber: number, body: BillingNoteBody): Promise<JobBillingNote> =>
  request<JobBillingNote>(`/jobs/${jobNumber}/billing/notes`, { method: 'POST', body });

// Change orders (design 20, slice 2, D-113): the register on a job's Change orders tab. Every write
// answers the whole register with the contract figures it moved; a stale version answers 409
// `stale_change_order`; a delete is refused 409 `linked` / `billed` once the row is in Jobber or billed.
export const fetchChangeOrders = (jobNumber: number, signal?: AbortSignal): Promise<ChangeOrdersResponse> =>
  request<ChangeOrdersResponse>(`/jobs/${jobNumber}/change-orders`, signal ? { signal } : {});
/** + Change order (design 37): the change order as Quoting and its quote asked of Jobber; 202 while Jobber makes it. */
export const createChangeOrder = (jobNumber: number, body: CreateChangeOrderBody): Promise<ChangeOrderCreatedResponse> =>
  request<ChangeOrderCreatedResponse>(`/jobs/${jobNumber}/change-orders`, { method: 'POST', body });
/** Mark declined or Reopen. */
export const setChangeOrderStatus = (jobNumber: number, id: number, body: ChangeOrderStatusBody): Promise<ChangeOrdersResponse> =>
  request<ChangeOrdersResponse>(`/jobs/${jobNumber}/change-orders/${id}/status`, { method: 'PUT', body });
export const saveChangeOrder = (jobNumber: number, id: number, body: EditChangeOrderBody): Promise<ChangeOrdersResponse> =>
  request<ChangeOrdersResponse>(`/jobs/${jobNumber}/change-orders/${id}`, { method: 'PUT', body });
/** Convert (design 37 §8 E): 202 with the register, the row Converting; 201 on a dry run. */
export const convertChangeOrder = (jobNumber: number, id: number, body: ConvertChangeOrderBody): Promise<ChangeOrderConvertResponse> =>
  request<ChangeOrderConvertResponse>(`/jobs/${jobNumber}/change-orders/${id}/convert`, { method: 'POST', body });
export const deleteChangeOrder = (jobNumber: number, id: number, body: VersionBody): Promise<ChangeOrdersResponse> =>
  request<ChangeOrdersResponse>(`/jobs/${jobNumber}/change-orders/${id}`, { method: 'DELETE', body });
